import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AiReviewConversationsHarvestingV2Job} from '../../../../../libs/shared/core/src/models/api/jobModel';
import {computeJobProgress} from '../../../../../libs/shared/core/src/models/helpers/jobHelpers';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerAction} from '../../../../../libs/shared/ui/src/components/banners/bannerAction';
import {BannerCloseAction} from '../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerContent} from '../../../../../libs/shared/ui/src/components/banners/bannerContent';
import {BannerContentTitle} from '../../../../../libs/shared/ui/src/components/banners/bannerContentTitle';
import {BannerFooterActions} from '../../../../../libs/shared/ui/src/components/banners/bannerFooterActions';
import {BannerIcon} from '../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {useLinkNavigation} from '../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {buildTeammatePath} from '../../../../helpers/modelHelpers';
import {InboxesFiltersEnum, TeammatesFoldersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {getUserId} from '../../../../store/data/teammates/teammatesSelectors';
import {helpCenterUrl} from '../../../../store/stateless/navigationActions';

/*
 * Props.
 */

interface AiReviewConversationsHarvestingBannerProps {
  job: AiReviewConversationsHarvestingV2Job;
  onDismissClick: (jobId: number) => void;
}

/*
 * Styles.
 */

const StyledBanner = styled(Banner)`
  width: 290px;
`;

/*
 * Component.
 */

export const AiReviewConversationsHarvestingBannerV2: FC<AiReviewConversationsHarvestingBannerProps> = (
  props,
) => {
  const {job, onDismissClick} = props;
  const theme = useUserTheme();

  const progress = computeJobProgress(job);
  const isJobDone = progress === 1;
  const {navigateToLink} = useLinkNavigation();
  const userId = useAppSelector(getUserId);
  const {hasHarvestedSamples} = job;

  const {color, iconName} = computeBannerStyles(isJobDone, hasHarvestedSamples, theme);

  return (
    <StyledBanner
      bannerType={BannerTypesEnum.MULTILINE}
      priority={BannerPrioritiesEnum.INFORMATION}
      color={color}
    >
      <BannerCloseAction onClick={() => onDismissClick(job.id)} />
      <BannerIcon iconName={iconName} />
      <BannerContent>{renderBannerContent(isJobDone, hasHarvestedSamples)}</BannerContent>
      <BannerFooterActions>
        <BannerAction
          onClick={() => {
            const bannerAction = computeBannerAction(isJobDone, hasHarvestedSamples, userId, navigateToLink);
            bannerAction();
            onDismissClick(job.id);
          }}
        >
          {renderBannerActionContent(isJobDone, hasHarvestedSamples)}
        </BannerAction>
      </BannerFooterActions>
    </StyledBanner>
  );
};

/*
 * Helpers.
 */

function renderBannerContent(isJobDone: boolean, hasHarvestedSamples: boolean) {
  if (isJobDone && !hasHarvestedSamples) {
    return (
      <>
        <BannerContentTitle>
          <Trans id="banner-ai-review-conversations-harvesting-v2-success-no-samples-title">
            Found no samples
          </Trans>
        </BannerContentTitle>
        <Trans id="banner-ai-review-conversations-harvesting-v2-success-no-samples-description">
          You&apos;ll get an email when new messages are found for AI training.
        </Trans>
      </>
    );
  }

  if (isJobDone && hasHarvestedSamples) {
    return (
      <>
        <BannerContentTitle>
          <Trans id="banner-ai-review-conversations-harvesting-v2-success-title">
            Done collecting samples
          </Trans>
        </BannerContentTitle>
        <Trans id="banner-ai-review-conversations-harvesting-v2-success-description">
          Check inbox for a training email
        </Trans>
      </>
    );
  }

  return (
    <Trans id="banner-ai-review-conversations-harvesting-v2-ongoing-description">
      You&apos;ll receive an email in your inbox to begin AI training shortly.
    </Trans>
  );
}

function renderBannerActionContent(isJobDone: boolean, hasHarvestedSamples: boolean) {
  if (isJobDone && hasHarvestedSamples) {
    return <Trans id="ai-harvesting-banner-go-to-inbox-cta">Go to inbox</Trans>;
  }

  return <Trans id="ai-harvesting-banner-learn-more-cta">Learn more</Trans>;
}

function computeBannerStyles(
  isJobDone: boolean,
  hasHarvestedSamples: boolean,
  theme: UserTheme,
): {
  color: string;
  iconName: IconName;
} {
  // Happy path.
  if (isJobDone && hasHarvestedSamples) {
    return {
      color: theme.banner.successBackground,
      iconName: 'checkmarkCircleSmall',
    };
  }

  // Slightly sad path—no samples found. Need to wait for more messages.
  if (isJobDone && !hasHarvestedSamples) {
    return {
      color: theme.banner.neutralBackground,
      iconName: 'infoFilledSmall',
    };
  }

  // Job still going
  return {
    color: theme.banner.action,
    iconName: 'sparklesAiFilled',
  };
}

function computeBannerAction(
  isJobDone: boolean,
  hasHarvestedSamples: boolean,
  userId: number,
  navigateToLink: (url: string) => void,
) {
  if (isJobDone && hasHarvestedSamples) {
    return () => {
      navigateToLink(buildTeammatePath(userId, TeammatesFoldersEnum.INBOX, InboxesFiltersEnum.OPEN));
    };
  }

  return () => {
    navigateToLink(`${helpCenterUrl}/en/articles/759488`);
  };
}
